import { motion, AnimatePresence } from 'framer-motion';
import { Network } from 'lucide-react';
import { AGENTS, AgentKey } from '../../lib/agents';
import { AgentAvatar } from './AgentAvatar';

export interface CommEvent {
  id: string;
  from: AgentKey;
  to: AgentKey;
  label: string;
}

interface AgentCommsPanelProps {
  events: CommEvent[];
  playing: boolean;
  className?: string;
}

/** Animated A2A / MCP communication view: shows the live handoff + a short history. */
export function AgentCommsPanel({ events, playing, className }: AgentCommsPanelProps) {
  const current = events[events.length - 1];
  const history = events.slice(Math.max(0, events.length - 5), events.length - 1).reverse();

  return (
    <div className={`rounded-2xl border border-white/[0.06] bg-background/60 backdrop-blur-xl p-4 ${className ?? ''}`}>
      <div className="flex items-center gap-2 mb-3">
        <Network className="w-4 h-4 text-vokentra" />
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Agent ↔ Agent / MCP</h3>
        {playing && <span className="ml-auto w-2 h-2 rounded-full bg-success animate-pulse" />}
      </div>

      {/* Live handoff */}
      {current ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <AgentAvatar agent={current.from} size={40} active />
              <div className="relative flex-1 h-[3px] rounded-full bg-white/10 overflow-hidden">
                <motion.span
                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                  style={{ background: AGENTS[current.to].color, boxShadow: `0 0 8px ${AGENTS[current.to].color}` }}
                  animate={{ left: ['0%', '100%'] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
              <AgentAvatar agent={current.to} size={40} active />
            </div>
            <p className="text-[11px] text-text-secondary text-center">
              <span style={{ color: AGENTS[current.from].color }}>{AGENTS[current.from].short}</span>
              {' → '}
              <span style={{ color: AGENTS[current.to].color }}>{AGENTS[current.to].short}</span>
              {' · '}
              <span className="text-text-muted">{current.label}</span>
            </p>
          </motion.div>
        </AnimatePresence>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-text-muted">
          <Network className="w-8 h-8 opacity-30" />
          <p className="text-[11px] mt-2">No active communication</p>
        </div>
      )}

      {/* Recent history */}
      {history.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1.5">
          {history.map((e, i) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 - i * 0.18 }}
              className="flex items-center gap-1.5 text-[10px] text-text-muted"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: AGENTS[e.from].color }} />
              <span>{AGENTS[e.from].short}</span>
              <span>→</span>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: AGENTS[e.to].color }} />
              <span>{AGENTS[e.to].short}</span>
              <span className="truncate opacity-80">· {e.label}</span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
